import React from 'react';
import { Select } from '@aidkitorg/component-library';
import { SelectOptionType } from '@aidkitorg/component-library/dist/types/src/Select';
import { useOrganizationContext } from '../../Contexts/OrganizationContext';
import { useLocalizedStrings } from '../../Localization';

export const OrganizationSelector: React.FC = () => {
  const { 
    organizations, 
    selectedOrganization, 
    setSelectedOrganization, 
    canSelectMultiple,
    loading 
  } = useOrganizationContext();
  const L = useLocalizedStrings();

  if (loading) {
    return (
      <div className="px-2 py-1">
        <div className="animate-pulse bg-gray-200 h-6 rounded w-32"></div>
      </div>
    );
  }

  if (!canSelectMultiple) {
    // If user can't select multiple, just show the organization name with label
    const org = organizations[0];
    if (org) {
      return (
        <div className="px-2">
          <div className="text-xs text-gray-500 mb-1">
            {L.admin_panel.organizations.organization}
          </div>
          <div className="font-semibold text-gray-900 truncate w-full">
            {org.name}
          </div>
        </div>
      );
    }
    return null;
  }

  const handleOrganizationChange = (value: SelectOptionType | null) => {
    if (!value || value.value === 'all') {
      setSelectedOrganization(undefined);
    } else if (value.value === 'no_org') {
      setSelectedOrganization(null);
    } else {
      const org = organizations.find(o => o.uid === value.value);
      if (org) {
        setSelectedOrganization(org);
      }
    }
  };

  // Build options for the Select component
  const options: SelectOptionType[] = [
    {
      label: L.admin_panel.organizations.all_organizations,
      value: 'all'
    },
    {
      label: L.admin_panel.organizations.no_organization,
      value: 'no_org'
    },
    ...organizations.map(org => ({
      label: org.name,
      value: org.uid
    }))
  ];

  const currentValue = selectedOrganization === undefined
    ? options[0] // "All organizations"
    : selectedOrganization === null
      ? options[1] // "No organization"
      : options.find(opt => opt.value === selectedOrganization.uid);

  return (
    <div className="[&_label]:text-xs [&_label]:text-gray-500">
      <Select
        value={currentValue}
        onChange={handleOrganizationChange}
        options={options}
        placeholder={L.admin_panel.organizations.organization}
        className="w-full text-sm" 
        description={L.admin_panel.organizations.select_organization} 
        label={L.admin_panel.organizations.organization}
        labelLocation="above" />
    </div>
  );
};